import type { ExamTypeConfig } from '../types';
// 注意：考试配置从 @examflow/exams 包加载，可以在运行时动态加载
// 使用 loadExamConfig(examId) 函数获取对应考试的配置

/**
 * 语言接口
 */
export interface Language {
  id: string; // 'french', 'english'
  name: string; // '法语', '英语'
  nameEn: string; // 'French', 'English'
  icon: string; // '🇫🇷', '🇬🇧'
  color: string; // Tailwind 颜色类
  exams: Exam[];
}

/**
 * 考试接口
 */
export interface Exam {
  id: string; // 'tcf', 'ielts'
  name: string; // 'TCF', 'IELTS'
  fullName: string; // 完整名称
  description: string;
  icon?: string;
  color: string;
  available: boolean; // 是否可用
  config?: ExamTypeConfig; // 考试配置（sections, scoring）- 可选，可以动态加载
}

/**
 * 考试结构配置
 * 定义语言 -> 考试 -> 题库的层次结构
 * 目前支持：英语、法语、西班牙语、日语、韩语
 */
export const EXAM_STRUCTURE: Language[] = [
  {
    id: 'english',
    name: '英语',
    nameEn: 'English',
    icon: '🇬🇧',
    color: 'from-red-500 to-red-600',
    exams: [
      {
        id: 'ielts',
        name: 'IELTS',
        fullName: 'International English Language Testing System',
        description: '雅思考试 - 国际英语语言测试系统，广泛用于留学和移民',
        color: 'from-red-500 to-red-600',
        available: true,
      },
      {
        id: 'toefl',
        name: 'TOEFL',
        fullName: 'Test of English as a Foreign Language',
        description: '托福考试 - 美国教育考试服务中心主办，主要用于美国留学',
        color: 'from-purple-500 to-purple-600',
        available: true,
      },
    ],
  },
  {
    id: 'french',
    name: '法语',
    nameEn: 'French',
    icon: '🇫🇷',
    color: 'from-blue-500 to-blue-600',
    exams: [
      {
        id: 'tcf',
        name: 'TCF',
        fullName: 'Test de Connaissance du Français',
        description: 'TCF 是法国教育部认可的法语水平测试，评估非法语母语者的法语能力。',
        icon: '🇫🇷',
        color: 'from-blue-500 to-blue-600',
        available: true,
        // config 可以动态加载，避免循环依赖
      },
      {
        id: 'delf',
        name: 'DELF/DALF',
        fullName: "Diplôme d'Études en Langue Française",
        description: '法语学习文凭 - 法国官方认证的法语能力证书',
        icon: '🎓',
        color: 'from-indigo-500 to-indigo-600',
        available: false, // 行为配置尚未落地；先不做 Content Inventory / 产线
      },
    ],
  },
  {
    id: 'spanish',
    name: '西班牙语',
    nameEn: 'Spanish',
    icon: '🇪🇸',
    color: 'from-yellow-500 to-yellow-600',
    exams: [
      {
        id: 'dele',
        name: 'DELE',
        fullName: 'Diplomas de Español como Lengua Extranjera',
        description: 'DELE 是西班牙官方认证的西班牙语水平证书，由塞万提斯学院颁发',
        icon: '🇪🇸',
        color: 'from-yellow-500 to-yellow-600',
        available: true,
      },
      {
        id: 'siele',
        name: 'SIELE',
        fullName: 'Servicio Internacional de Evaluación de la Lengua Española',
        description: 'SIELE 是国际西班牙语评估服务，提供在线西班牙语能力认证',
        icon: '🌐',
        color: 'from-orange-500 to-orange-600',
        available: true,
      },
    ],
  },
  {
    id: 'japanese',
    name: '日语',
    nameEn: 'Japanese',
    icon: '🇯🇵',
    color: 'from-pink-500 to-pink-600',
    exams: [
      {
        id: 'jlpt',
        name: 'JLPT',
        fullName: 'Japanese Language Proficiency Test',
        description: '日本语能力测试 - 国际通用的日语能力认证考试',
        icon: '🇯🇵',
        color: 'from-pink-500 to-pink-600',
        available: true,
      },
    ],
  },
  {
    id: 'korean',
    name: '韩语',
    nameEn: 'Korean',
    icon: '🇰🇷',
    color: 'from-cyan-500 to-cyan-600',
    exams: [
      {
        id: 'topik',
        name: 'TOPIK',
        fullName: 'Test of Proficiency in Korean',
        description: '韩国语能力考试 - 韩国官方认证的韩语水平测试',
        icon: '🇰🇷',
        color: 'from-cyan-500 to-cyan-600',
        available: true,
      },
    ],
  },
];

/**
 * 获取所有语言
 */
export function getLanguages(): Language[] {
  return EXAM_STRUCTURE;
}

/**
 * 根据语言 ID 获取语言
 */
export function getLanguage(languageId: string): Language | undefined {
  return getLanguages().find((lang) => lang.id === languageId);
}

/**
 * 获取语言下的所有考试
 */
export function getExamsByLanguage(languageId: string): Exam[] {
  return getLanguage(languageId)?.exams || [];
}

/**
 * 根据考试 ID 获取考试（跨语言搜索）
 * 返回语言和考试信息
 */
export function getExam(examId: string): { language: Language; exam: Exam } | undefined {
  for (const language of EXAM_STRUCTURE) {
    const exam = language.exams.find((e) => e.id === examId);
    if (exam) {
      return { language, exam };
    }
  }
  return undefined;
}

/**
 * 获取所有可用的考试（扁平化）
 */
export function getAllAvailableExams(): Exam[] {
  return EXAM_STRUCTURE.flatMap((lang) => lang.exams.filter((exam) => exam.available));
}

/**
 * 获取所有考试（扁平化，包括未启用的）
 */
export function getAllExams(): Exam[] {
  return EXAM_STRUCTURE.flatMap((lang) => lang.exams);
}
